// frontend-react/src/components/Header.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserRoleDisplay } from '../services/authService';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { Tooltip, Select, message } from 'antd';
import './Header.css';

// ✅ ISO 規範：姓名遮蔽工具 (保留首尾，中間遮蔽)
const maskName = (name: string) => {
  if (!name) return '';
  const str = name.trim();
  if (str.length <= 2) return str.substring(0, 1) + '*';
  return str[0] + '*'.repeat(str.length - 2) + str[str.length - 1];
};

interface HeaderProps {
  currentPage?: string;
  minimal?: boolean;
}

const Header: React.FC<HeaderProps> = ({ currentPage, minimal = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, switchCompany } = useAuth();
  const { faqEnabled, canUseAdReview, canUseContractComparison } = useFeatureFlags();
  const [showUserMenuDropdown, setShowUserMenuDropdown] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleSwitchCompany = async (companyId: number) => {
    try {
      await switchCompany(companyId);
      message.success('已切換公司');
    } catch {
      message.error('切換公司失敗');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenuDropdown(false);
      }
    };
    if (showUserMenuDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenuDropdown]);

  const getSystemTitle = () => {
    if (!user?.department) return '律果科技法務審閱系統';
    const departmentTitles: Record<string, string> = {
      和泰汽車: '和泰汽車法務審閱系統',
      崇越科技: '崇越科技法務審閱系統',
      冠德建設: '冠德建設法務審閱系統',
      律果科技: '律果科技法務審閱系統',
    };
    return departmentTitles[user.department] || '律果科技法務審閱系統';
  };

  const getPermissionBadge = () => {
    if (!user?.permissions) return '';
    if (user.permissions.can_access_all_features) return '完整功能';
    return '';
  };

  const isActivePage = (path: string) => {
    if (currentPage) return currentPage === path;
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const isAnyUserManagementPageActive = () =>
    isActivePage('/user-management') || isActivePage('/department-management');

  const navigateToPage = (path: string) => {
    if ((path === '/ad-review' || path === '/contract-comparison') && isActivePage(path)) {
      navigate(path, { state: { resetKey: Date.now() } });
    } else {
      navigate(path);
    }
    setShowUserMenuDropdown(false);
  };

  const handleLogoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate('/');
    }
  };

  // 取得顯示姓名 (已遮罩)
  const getMaskedName = () => {
    if (!user) return '';
    let rawName = user.name;
    return maskName(rawName);
  };

  return (
    <header className='header'>
      <div className='header-left'>
        <div
          className='company-logo'
          style={{ backgroundImage: `url(/images/robot-logo.png)`, cursor: 'pointer' }}
          aria-label='律果科技 Logo'
          role='button'
          tabIndex={0}
          onClick={() => navigate('/')}
          onKeyDown={handleLogoKeyDown}
        />
        <div
          className='system-title'
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
          role='button'
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') navigate('/');
          }}
        >
          {getSystemTitle()}
        </div>
        {!minimal && (
          <nav className='header-nav'>
            {canUseAdReview && (
              <button
                className={`nav-item ${isActivePage('/ad-review') ? 'active' : ''}`}
                onClick={() => navigateToPage('/ad-review')}
              >
                全文審閱
              </button>
            )}
            {canUseContractComparison && (
              <button
                className={`nav-item ${isActivePage('/contract-comparison') ? 'active' : ''}`}
                onClick={() => navigateToPage('/contract-comparison')}
              >
                合約比對
              </button>
            )}
            {user &&
              (user.permissions?.can_manage_users ||
                user.roles?.includes('ADMIN') ||
                user.roles?.includes('SUPER_ADMIN') ||
                user.is_department_manager) && (
                <div className='nav-item-dropdown' ref={userMenuRef}>
                  <button
                    className={`nav-item ${isAnyUserManagementPageActive() ? 'active' : ''}`}
                    onClick={() => setShowUserMenuDropdown(!showUserMenuDropdown)}
                  >
                    {user.permissions?.can_manage_users ||
                    user.roles?.includes('ADMIN') ||
                    user.roles?.includes('SUPER_ADMIN')
                      ? '使用者管理 ▼'
                      : '我的部門 ▼'}
                  </button>
                  {showUserMenuDropdown && (
                    <div className='dropdown-menu'>
                      {(user.permissions?.can_manage_users ||
                        user.roles?.includes('ADMIN') ||
                        user.roles?.includes('SUPER_ADMIN')) && (
                        <button
                          className={`dropdown-item ${isActivePage('/user-management') ? 'active' : ''}`}
                          onClick={() => navigateToPage('/user-management')}
                        >
                          使用者列表
                        </button>
                      )}
                      <button
                        className={`dropdown-item ${isActivePage('/department-management') ? 'active' : ''}`}
                        onClick={() => navigateToPage('/department-management')}
                      >
                        {user.permissions?.can_manage_users ||
                        user.roles?.includes('ADMIN') ||
                        user.roles?.includes('SUPER_ADMIN')
                          ? '部門管理'
                          : '我的部門'}
                      </button>
                    </div>
                  )}
                </div>
              )}

            {user?.is_superadmin && (
              <button
                className={`nav-item ${isActivePage('/company-management') ? 'active' : ''}`}
                onClick={() => navigateToPage('/company-management')}
              >
                公司管理
              </button>
            )}

            {faqEnabled && (
              <button
                className={`nav-item ${isActivePage('/faq') ? 'active' : ''}`}
                onClick={() => navigateToPage('/faq')}
              >
                常見問答
              </button>
            )}
          </nav>
        )}
      </div>

      {!minimal && (
        <div className='header-right'>
          {isAuthenticated && user ? (
            <>
              {user.is_superadmin && user.all_companies && user.all_companies.length > 0 && (
                <Select
                  style={{ minWidth: 120 }}
                  value={user.all_companies.find(c => c.code === user.company_code)?.id ?? null}
                  onChange={handleSwitchCompany}
                  options={user.all_companies.map(c => ({ value: c.id, label: c.name }))}
                  size='small'
                  popupMatchSelectWidth={false}
                />
              )}

              <div className='user-permission'>
                <div className='user-role'>{getUserRoleDisplay(user)}</div>
                <div className='user-perms'>{getPermissionBadge()}</div>
              </div>

              <div className='user-info'>
                <span className='user-name'>
                  {getMaskedName()}
                  {user.is_department_manager && <span className='manager-tag'>(主管)</span>}
                </span>
                <button className='logout-btn' onClick={logout}>
                  登出
                </button>
              </div>

              {/* ✅ 修改重點：滑鼠移到頭像才顯示 Email 詳細資訊 */}
              <Tooltip
                title={
                  <div style={{ padding: '4px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{user.name}</div>
                    <div style={{ fontSize: '12px', color: '#e6f4f8' }}>{user.email}</div>
                  </div>
                }
                placement='bottomRight'
                arrow={{ pointAtCenter: true }}
              >
                <div className='user-avatar'>{(user.name || '?').charAt(0).toUpperCase()}</div>
              </Tooltip>
            </>
          ) : (
            <button className='login-btn' onClick={() => navigate('/login')}>
              登入
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
